import React, { useMemo, useRef, useState } from 'react';
import { Heart, BookOpen, Download, Share2, Sparkles, Copy, Check, Users, Puzzle, Zap, Sprout, Flame, MessageCircle, Target, RefreshCw, AlertTriangle, Compass, User } from 'lucide-react';
import html2canvas from 'html2canvas';
import { gerarEncontro } from '../utils/relacionalEngine';
import BussolaVivaEncontro from './BussolaVivaEncontro';
import CartaDoEncontro from './CartaDoEncontro';
import { ESSENCIA, FRASE_SEMENTE, TENSAO_RAIZ } from '../data/archetypeContent';

// A Bússola do Encontro — a leitura de dois corações.
// Uso:  <BussolaDoEncontro resultA={rA} resultB={rB} nomeA="Ana" nomeB="Léo" />
const COR_A = '#EC4899';   // rosa
const COR_B = '#38BDF8';   // azul-céu

const BussolaDoEncontro = ({ resultA, resultB, nomeA, nomeB, codigo }) => {
  const enc = useMemo(() => gerarEncontro(resultA, resultB, { nomeA, nomeB }), [resultA, resultB, nomeA, nomeB]);
  const ref = useRef(null);
  const [baixando, setBaixando] = useState(false);
  const [copiado, setCopiado] = useState(false);
  if (!enc) return null;

  const baixar = async () => {
    if (!ref.current) return; setBaixando(true);
    try {
      const canvas = await html2canvas(ref.current, { backgroundColor:'#160f2e', scale:2, useCORS:true, logging:false });
      const a = document.createElement('a'); a.href = canvas.toDataURL('image/png'); a.download = 'bussola-do-encontro.png'; a.click();
    } catch (e) { /* silencioso */ } finally { setBaixando(false); }
  };
  const compartilhar = async () => {
    const txt = `${enc.nomeA} e ${enc.nomeB} fizeram a Bússola do Encontro 💞\n\n“${enc.semente}”\n\nFaçam a de vocês:`;
    const url = 'https://estacoes.ocoracaodetodasascoisas.com';
    try { if (navigator.share) await navigator.share({ title:'A Bússola do Encontro', text:txt, url }); else { await navigator.clipboard.writeText(`${txt} ${url}`); alert('Copiado!'); } } catch (e) {}
  };

  const copiarLinkBussola = async () => {
    try { await navigator.clipboard.writeText(`${window.location.origin}/encontro/${codigo}`); setCopiado(true); setTimeout(() => setCopiado(false), 2000); } catch (e) {}
  };

  const Secao = ({ titulo, sub, icon: Icon, iconEmoji, gradFrom, gradTo, borderColor, children }) => (
    <div className="mt-10">
      <div className="flex justify-center mb-5">
        <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full border backdrop-blur-sm"
             style={{ 
               background: `linear-gradient(135deg, ${gradFrom || 'rgba(255,255,255,0.05)'}, ${gradTo || 'rgba(255,255,255,0.02)'})`,
               borderColor: borderColor || 'rgba(255,255,255,0.15)'
             }}>
          {iconEmoji && <span className="text-2xl">{iconEmoji}</span>}
          {Icon && <Icon className="w-6 h-6" style={{ color: borderColor || 'rgba(255,255,255,0.8)' }} />}
          <div className="text-center">
            <h3 className="text-xl md:text-2xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>{titulo}</h3>
            {sub && <p className="text-white/50 text-xs mt-0.5">{sub}</p>}
          </div>
        </div>
      </div>
      {children}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#160f2e] via-indigo-950 to-[#0a1628] py-8 px-4" data-testid="bussola-do-encontro">
      {codigo && (
        <div className="max-w-3xl mx-auto mb-4">
          <div className="bg-white/[0.05] rounded-2xl p-4 border border-rose-500/20 text-center">
            <p className="text-white/75 text-sm mb-2">💾 Guardem este link — para reverem a Bússola e enviarem um ao outro:</p>
            <div className="bg-white/5 rounded-lg p-2.5 border border-white/10 break-all text-white/80 text-xs mb-3">{window.location.origin}/encontro/{codigo}</div>
            <button onClick={copiarLinkBussola} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-gradient-to-r from-rose-500 to-indigo-500 text-white text-sm font-semibold" style={{ border: 'none', cursor: 'pointer' }}>
              {copiado ? (<><Check className="w-4 h-4" /> Link copiado!</>) : (<><Copy className="w-4 h-4" /> Copiar link da Bússola</>)}
            </button>
          </div>
        </div>
      )}
      <div className="max-w-3xl mx-auto" ref={ref}>
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-rose-900/40 to-indigo-900/40 border border-rose-500/20">
            <Heart className="w-6 h-6 text-rose-300" />
            <h1 className="text-2xl md:text-3xl font-extrabold text-white" style={{ fontFamily:"'Playfair Display', serif" }}>A Bússola do Encontro</h1>
          </div>
          <p className="text-white/60 mt-2">
            <span style={{ color: COR_A }} className="font-semibold">{enc.nomeA}</span>
            <span className="text-white/40"> & </span>
            <span style={{ color: COR_B }} className="font-semibold">{enc.nomeB}</span>
          </p>
          {enc.selo && enc.selo !== 'alta' && (
            <p className="text-white/40 text-xs mt-1">Leitura mais precisa quando os dois respondem com calma (confiança: {enc.selo}).</p>
          )}
        </div>

        {/* Bússola dupla — barras pareadas por estação */}
        <div className="bg-white/[0.04] rounded-2xl p-5 md:p-6 border border-white/10">
          <div className="flex items-center justify-center gap-5 mb-4 text-sm">
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full" style={{ background: COR_A }} /> <span className="text-white/80">{enc.nomeA}</span></span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full" style={{ background: COR_B }} /> <span className="text-white/80">{enc.nomeB}</span></span>
          </div>
          <div className="space-y-3">
            {enc.panorama.map(p => (
              <div key={p.root}>
                <div className="flex justify-between text-xs text-white/60 mb-1"><span>{p.nome}</span></div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2.5 bg-white/5 rounded-full overflow-hidden"><div className="h-full rounded-full" style={{ width:`${p.a}%`, background: COR_A }} /></div>
                    <span className="text-white/40 text-xs w-9 text-right">{p.a}%</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2.5 bg-white/5 rounded-full overflow-hidden"><div className="h-full rounded-full" style={{ width:`${p.b}%`, background: COR_B }} /></div>
                    <span className="text-white/40 text-xs w-9 text-right">{p.b}%</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bússola Viva — radar com os dois perfis */}
        <BussolaVivaEncontro panorama={enc.panorama} nomeA={enc.nomeA} nomeB={enc.nomeB} />

        {/* ===== PERFIS INDIVIDUAIS — Quem é cada um ===== */}
        {(() => {
          const NOMES_RAIZ = { peregrino: 'Peregrino', guardiao: 'Guardião', amante: 'Amante', alquimista: 'Alquimista' };
          const EMOJIS_RAIZ = { peregrino: '🌿', guardiao: '🏠', amante: '💕', alquimista: '✨' };
          const CORES_RAIZ = { peregrino: '#22C55E', guardiao: '#A855F7', amante: '#EC4899', alquimista: '#F59E0B' };
          
          // Identificar arquétipo dominante de cada um (a maior estação)
          const getDominante = (panorama, pessoa) => {
            const key = pessoa === 'a' ? 'a' : 'b';
            let max = 0, dominante = 'peregrino';
            panorama.forEach(p => {
              if (p[key] > max) { max = p[key]; dominante = p.root; }
            });
            return dominante;
          };
          
          const domA = getDominante(enc.panorama, 'a');
          const domB = getDominante(enc.panorama, 'b');
          
          const PerfilCard = ({ nome, dominante, cor, isA }) => (
            <div className="flex-1 min-w-[280px]">
              <div 
                className="rounded-2xl p-5 border h-full"
                style={{ 
                  background: `linear-gradient(135deg, ${CORES_RAIZ[dominante]}15, ${CORES_RAIZ[dominante]}05)`,
                  borderColor: `${CORES_RAIZ[dominante]}30`
                }}
              >
                {/* Nome e badge */}
                <div className="flex items-center gap-3 mb-4">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center text-lg"
                    style={{ backgroundColor: cor + '30', border: `2px solid ${cor}` }}
                  >
                    {EMOJIS_RAIZ[dominante]}
                  </div>
                  <div>
                    <p className="font-bold text-white" style={{ color: cor }}>{nome}</p>
                    <p className="text-white/50 text-xs">{NOMES_RAIZ[dominante]} — Estação dominante</p>
                  </div>
                </div>
                
                {/* Essência */}
                <div className="mb-4">
                  <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1">A essência</p>
                  <p className="text-white/85 text-sm leading-relaxed italic">
                    "{ESSENCIA[dominante]}"
                  </p>
                </div>
                
                {/* Frase-semente */}
                <div className="mb-4">
                  <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1">A frase que carrega</p>
                  <p className="text-white/75 text-sm leading-relaxed">
                    "{FRASE_SEMENTE[dominante]}"
                  </p>
                </div>
                
                {/* Tensão pessoal */}
                <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                  <p className="text-amber-300/70 text-[10px] uppercase tracking-wider mb-1 flex items-center gap-1">
                    <span>⚡</span> A tensão pessoal
                  </p>
                  <p className="text-white/70 text-xs leading-relaxed">
                    {TENSAO_RAIZ[dominante]}
                  </p>
                </div>
              </div>
            </div>
          );
          
          return (
            <div className="mt-10">
              <div className="flex justify-center mb-6">
                <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full border backdrop-blur-sm"
                     style={{ 
                       background: 'linear-gradient(135deg, rgba(255,255,255,0.08), rgba(255,255,255,0.02))',
                       borderColor: 'rgba(255,255,255,0.15)'
                     }}>
                  <User className="w-5 h-5 text-white/70" />
                  <div className="text-center">
                    <h3 className="text-xl md:text-2xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                      Quem é cada um
                    </h3>
                    <p className="text-white/50 text-xs mt-0.5">O coração individual antes do encontro</p>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-col md:flex-row gap-4">
                <PerfilCard nome={enc.nomeA} dominante={domA} cor={COR_A} isA={true} />
                <PerfilCard nome={enc.nomeB} dominante={domB} cor={COR_B} isA={false} />
              </div>
              
              <p className="text-center text-white/40 text-xs mt-5 italic">
                Conhecer a si mesmo é o primeiro passo para conhecer o outro.
              </p>
            </div>
          );
        })()}

        {/* Onde se encontram */}
        {enc.encontros.length > 0 && (
          <Secao titulo="Onde vocês se encontram" sub="Estações fortes em comum" iconEmoji="🤝" gradFrom="rgba(16,185,129,0.15)" gradTo="rgba(16,185,129,0.05)" borderColor="rgba(16,185,129,0.4)">
            <div className="space-y-3">
              {enc.encontros.map(e => (
                <div key={e.root} className="bg-emerald-900/20 rounded-xl p-4 border border-emerald-500/20">
                  <p className="text-emerald-200 font-semibold text-sm mb-1 flex items-center gap-2">
                    <span className="text-lg">{e.root === 'peregrino' ? '🚶' : e.root === 'guardiao' ? '🏠' : e.root === 'amante' ? '💕' : '✨'}</span>
                    {e.nome}
                  </p>
                  <p className="text-white/80 text-sm leading-relaxed">{e.texto}</p>
                </div>
              ))}
            </div>
          </Secao>
        )}

        {/* Onde se complementam */}
        {enc.complementos.length > 0 && (
          <Secao titulo="Onde vocês se complementam" sub="O que falta em um, mora no outro" icon={Puzzle} gradFrom="rgba(99,102,241,0.15)" gradTo="rgba(99,102,241,0.05)" borderColor="rgba(99,102,241,0.4)">
            <div className="space-y-3">
              {enc.complementos.map(c => (
                <div key={c.root} className="bg-indigo-900/20 rounded-xl p-4 border border-indigo-500/20">
                  <p className="text-indigo-200 font-semibold text-sm mb-1 flex items-center gap-2">
                    <span className="text-lg">{c.root === 'peregrino' ? '🚶' : c.root === 'guardiao' ? '🏠' : c.root === 'amante' ? '💕' : '✨'}</span>
                    {c.nome}
                  </p>
                  <p className="text-white/80 text-sm leading-relaxed">{c.texto}</p>
                </div>
              ))}
            </div>
          </Secao>
        )}

        {/* A tensão do casal */}
        {enc.tensao && (
          <Secao titulo="A tensão que move vocês" sub="Não é incompatibilidade — é a dança a dois" icon={Zap} gradFrom="rgba(245,158,11,0.15)" gradTo="rgba(244,63,94,0.1)" borderColor="rgba(245,158,11,0.4)">
            <div className="bg-gradient-to-br from-amber-900/20 to-rose-900/20 rounded-xl p-5 border border-amber-500/20">
              <p className="text-white/85 text-base leading-relaxed italic text-center">{enc.tensao.texto}</p>
            </div>
          </Secao>
        )}

        {/* Como cuidar um do outro */}
        {enc.cuidado.length > 0 && (
          <Secao titulo="Como cuidar um do outro" sub="O presente que cada um pode oferecer" icon={Sprout} gradFrom="rgba(34,197,94,0.15)" gradTo="rgba(34,197,94,0.05)" borderColor="rgba(34,197,94,0.4)">
            <div className="space-y-3">
              {enc.cuidado.map((c, i) => (
                <div key={i} className="bg-gradient-to-r from-green-900/20 to-emerald-900/20 rounded-xl p-4 border border-green-500/20 relative overflow-hidden">
                  <div className="absolute top-2 right-3 text-2xl opacity-20">🌱</div>
                  <p className="text-green-200/90 font-semibold text-sm mb-1 flex items-center gap-2">
                    <span className="text-base">💧</span>
                    {c.de} <span className="text-green-400">→</span> {c.para}
                  </p>
                  <p className="text-white/80 text-sm leading-relaxed">{c.texto}</p>
                </div>
              ))}
            </div>
          </Secao>
        )}

        {/* NOVO: Pontos Fortes do Casal */}
        {enc.pontosFortes && (
          <Secao titulo="A força de vocês dois" sub="O que vocês constroem juntos" icon={Flame} gradFrom="rgba(239,68,68,0.15)" gradTo="rgba(249,115,22,0.1)" borderColor="rgba(239,68,68,0.4)">
            <div className="bg-gradient-to-br from-red-950/30 to-orange-950/30 rounded-xl p-5 border border-red-500/20">
              <p className="text-orange-200 font-semibold text-sm mb-2 flex items-center gap-2">
                🔥 Estação predominante: {enc.pontosFortes.nome}
              </p>
              <p className="text-white/85 text-sm leading-relaxed">{enc.pontosFortes.texto}</p>
            </div>
          </Secao>
        )}

        {/* NOVO: O Espelho — como um reflete o outro */}
        {enc.espelho && (
          <Secao titulo="O espelho entre vocês" sub="Como um reflete o outro" iconEmoji="🪞" gradFrom="rgba(168,85,247,0.15)" gradTo="rgba(139,92,246,0.1)" borderColor="rgba(168,85,247,0.4)">
            <div className="bg-gradient-to-br from-purple-950/30 to-violet-950/30 rounded-xl p-5 border border-purple-500/20">
              <p className="text-purple-200 font-semibold text-sm mb-2">{enc.espelho.titulo}</p>
              <p className="text-white/85 text-sm leading-relaxed">{enc.espelho.texto}</p>
            </div>
          </Secao>
        )}

        {/* NOVO: O que um ensina ao outro */}
        {enc.ensinoMutuo && (
          <Secao titulo="O que um ensina ao outro" sub="A troca que vocês fazem" icon={RefreshCw} gradFrom="rgba(6,182,212,0.15)" gradTo="rgba(34,211,238,0.1)" borderColor="rgba(6,182,212,0.4)">
            <div className="bg-gradient-to-br from-cyan-950/30 to-teal-950/30 rounded-xl p-5 border border-cyan-500/20">
              <p className="text-white/85 text-sm leading-relaxed">{enc.ensinoMutuo}</p>
            </div>
          </Secao>
        )}

        {/* NOVO: Pergunta Provocativa */}
        {enc.pergunta && (
          <Secao titulo="Uma pergunta para vocês" sub="Para conversar a dois" icon={MessageCircle} gradFrom="rgba(236,72,153,0.15)" gradTo="rgba(244,114,182,0.1)" borderColor="rgba(236,72,153,0.4)">
            <div className="bg-gradient-to-br from-pink-950/30 to-rose-950/30 rounded-xl p-6 border border-pink-500/20 text-center">
              <p className="text-white text-lg leading-relaxed" style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}>
                "{enc.pergunta}"
              </p>
              <p className="text-pink-300/50 text-xs mt-4">Não precisa responder agora. Deixe a pergunta trabalhar.</p>
            </div>
          </Secao>
        )}

        {/* NOVO: O que desenvolver juntos */}
        {enc.desenvolver && (
          <Secao titulo="O que desenvolver juntos" sub="O ponto cego do casal" icon={Target} gradFrom="rgba(59,130,246,0.15)" gradTo="rgba(96,165,250,0.1)" borderColor="rgba(59,130,246,0.4)">
            <div className="bg-gradient-to-br from-blue-950/30 to-sky-950/30 rounded-xl p-5 border border-blue-500/20">
              <p className="text-blue-200 font-semibold text-sm mb-2 flex items-center gap-2">
                🎯 Estação a cultivar: {enc.desenvolver.nome}
              </p>
              <p className="text-white/85 text-sm leading-relaxed">{enc.desenvolver.texto}</p>
            </div>
          </Secao>
        )}

        {/* NOVO: Prática para o casal */}
        {enc.pratica && (
          <Secao titulo="Uma prática para vocês" sub="Algo para experimentar juntos" icon={Sparkles} gradFrom="rgba(251,191,36,0.15)" gradTo="rgba(245,158,11,0.1)" borderColor="rgba(251,191,36,0.4)">
            <div className="bg-gradient-to-br from-amber-950/30 to-yellow-950/30 rounded-xl p-5 border border-amber-500/20">
              <p className="text-amber-200 font-semibold text-sm mb-2 flex items-center gap-2">
                ✨ Sugestão para o {enc.pratica.nome}
              </p>
              <p className="text-white/85 text-sm leading-relaxed italic">"{enc.pratica.texto}"</p>
            </div>
          </Secao>
        )}

        {/* NOVO: Risco se não cuidarem (só se houver tensão) */}
        {enc.tensao && enc.tensao.risco && (
          <Secao titulo="Atenção amorosa" sub="O cuidado que a relação pede" icon={AlertTriangle} gradFrom="rgba(249,115,22,0.15)" gradTo="rgba(234,88,12,0.1)" borderColor="rgba(249,115,22,0.4)">
            <div className="bg-gradient-to-br from-orange-950/30 to-red-950/30 rounded-xl p-5 border border-orange-500/20">
              <p className="text-orange-200/90 text-sm leading-relaxed">{enc.tensao.risco}</p>
            </div>
          </Secao>
        )}

        {/* A Carta do Encontro */}
        <CartaDoEncontro enc={enc} />


        {/* Pelo livro */}
        {enc.capitulos.length > 0 && (
          <Secao titulo="Para viverem juntos através do livro" sub="Capítulos recomendados para o casal" icon={BookOpen} gradFrom="rgba(120,53,15,0.2)" gradTo="rgba(146,64,14,0.1)" borderColor="rgba(180,83,9,0.4)">
            <div className="space-y-3">
              {enc.capitulos.map((c, i) => (
                <div key={i} className="bg-gradient-to-r from-amber-950/30 to-orange-950/30 rounded-xl p-4 border border-amber-700/30 relative overflow-hidden">
                  <div className="absolute top-2 right-3 text-2xl opacity-15">📖</div>
                  <p className="text-amber-200 font-bold text-sm mb-1">{c.capitulo}</p>
                  <p className="text-white/70 text-xs italic">{c.promessa}</p>
                  <p className="text-amber-400/50 text-xs mt-2">{c.porque}</p>
                </div>
              ))}
            </div>
          </Secao>
        )}

      </div>

      <div className="flex flex-col sm:flex-row justify-center items-center gap-3 mt-7 max-w-3xl mx-auto">
        <button onClick={baixar} disabled={baixando}
          className="flex items-center gap-2.5 text-white rounded-full px-8 py-4 text-base font-bold transition-all hover:scale-105"
          style={{ background: baixando ? 'rgba(236,72,153,0.4)' : 'linear-gradient(135deg, #EC4899, #6366F1)', boxShadow:'0 4px 24px rgba(236,72,153,0.3)', cursor: baixando?'not-allowed':'pointer', border:'none' }}>
          <Download className="w-5 h-5" /> {baixando ? 'Gerando…' : 'Baixar a bússola'}
        </button>
        <button onClick={compartilhar}
          className="flex items-center gap-2.5 text-white/90 border border-white/20 rounded-full px-8 py-4 text-base font-bold transition-all hover:bg-white/10 hover:scale-105" style={{ cursor:'pointer' }}>
          <Share2 className="w-5 h-5" /> Compartilhar
        </button>
      </div>
    </div>
  );
};
export default BussolaDoEncontro;
